import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapAction,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  markFormGroupsTouched,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  INWEPCombustionSourceControlName,
  INWEPNewRequestControlName,
  INWEPNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { INWEPNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  INWEPNewRequest,
  INWEPNewRequestDto,
  INWEPNewRequestFormWorkspaceState,
} from '../services/new-request/new-request.model';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-lbrc-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class INWEPNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = INWEPNewRequestControlName;
  combustionSourceControlName = INWEPCombustionSourceControlName;
  override formGroup: FormGroup<INWEPNewRequestFormGroup>;
  override portalId = PortalId.InstallationNotificationWithoutEmissionPermit;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('districtName', 'GK.MAP.DISTRICT'),
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('area', 'GK.MAP.AREA_IN_HA'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    false,
    true,
    '25',
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.InstallationNotificationWithoutEmissionPermitFormVersion;
  workspaceStateId =
    WorkspaceStateId.InstallationNotificationWithoutEmissionPermitForm;
  otherFileRequired = false;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private inwepNewRequestFormService: INWEPNewRequestFormService,
    public override streetService: StreetService,
    private toastr: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
    private cdr: ChangeDetectorRef,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
    this.initCombustionSourceFormGroupOnFormValueChanges();
  }

  createForm(): void {
    this.formGroup = this.inwepNewRequestFormService.getFormGroup();
  }

  initCombustionSourceFormGroupOnFormValueChanges(): void {
    this.getIsCombustionSourceRequiredFormControl()
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((value) => {
        if (value) {
          this.formGroup.addControl(
            this.controlName.CombustionSource,
            this.inwepNewRequestFormService.getCombustionSourceFormGroup(),
          );
          this.setOtherFilesRequiredOnCommissionDateUnknownFormControlChange();
        } else {
          this.formGroup.removeControl(this.controlName.CombustionSource);
          this.otherFileRequired = false;
        }
      });
  }

  setOtherFilesRequiredOnCommissionDateUnknownFormControlChange(): void {
    this.getCommissionDateUnknownFormControl()
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((isUnknown) => {
        this.otherFileRequired = isUnknown;
        this.cdr.detectChanges();
      });
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.InstallationNotificationWithoutEmissionPermit,
      this.mapStateService.getViewState(
        MapId.InstallationNotificationWithoutEmissionPermit,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.InstallationNotificationWithoutEmissionPermit,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      this.isMapSectionValid()
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/zgloszenieInstalacjiEmisyjnejBezWymoguZezwolenia/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<INWEPNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastr.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        INWEPNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): INWEPNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    parcels: this.getLandParcels(),
  });

  handleLoadWorkspaceResponse(state: INWEPNewRequestFormWorkspaceState): void {
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    if (state.parcels) {
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
      );
    }
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getFormValue(): INWEPNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.InstallationNotificationWithoutEmissionPermit}/requests-list`,
    );
  }

  isMapSectionValid(): boolean {
    return this.areLandParcelsChosen();
  }

  areLandParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  isActivityDescriptionFormControlValid(): boolean {
    return this.getActivityDescriptionFormControl().valid;
  }

  getActivityDescriptionFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ActivityDescription,
    ) as FormControl;
  }

  isInstallationOperatingTimeFormControlValid(): boolean {
    return this.getInstallationOperatingTimeFormControl().valid;
  }

  getInstallationOperatingTimeFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.InstallationOperatingTime,
    ) as FormControl;
  }

  isEmissionTypeAndAmountFormControlValid(): boolean {
    return this.getEmissionTypeAndAmountFormControl().valid;
  }

  getEmissionTypeAndAmountFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.EmissionTypeAndAmount,
    ) as FormControl;
  }

  isEmissionReductionMethodsFormControlValid(): boolean {
    return this.getEmissionReductionMethodsFormControl().valid;
  }

  getEmissionReductionMethodsFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.EmissionReductionMethods,
    ) as FormControl;
  }

  isReductionCompliantDescriptionFormControlValid(): boolean {
    return this.getReductionCompliantDescriptionFormControl().valid;
  }

  getReductionCompliantDescriptionFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.ReductionCompliantDescription,
    ) as FormControl;
  }

  getIsCombustionSourceRequiredFormControl(): FormControl {
    return this.formGroup.get(
      this.controlName.IsCombustionSourceRequired,
    ) as FormControl;
  }

  isBusinessActivityCodeFormControlValid(): boolean {
    return this.getBusinessActivityCodeFormControl().valid;
  }

  getBusinessActivityCodeFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.BusinessActivityCode,
    ) as FormControl;
  }

  isRegonFormControlValid(): boolean {
    return this.getRegonFormControl()?.valid;
  }

  getRegonFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.Regon,
    ) as FormControl;
  }

  isCombustionPowerFormControlValid(): boolean {
    return this.getCombustionPowerFormControl()?.valid;
  }

  getCombustionPowerFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.CombustionPower,
    ) as FormControl;
  }

  isCombustionSourceTypeFormControlValid(): boolean {
    return this.getCombustionSourceTypeFormControl()?.valid;
  }

  getCombustionSourceTypeFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.CombustionSourceType,
    ) as FormControl;
  }

  isFuelShareTypeFormControlValid(): boolean {
    return this.getFuelShareTypeFormControl()?.valid;
  }

  getFuelShareTypeFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.FuelShareType,
    ) as FormControl;
  }

  isEmissionStandardsFormControlValid(): boolean {
    return this.getEmissionStandardsFormControl()?.valid;
  }

  getEmissionStandardsFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.EmissionStandards,
    ) as FormControl;
  }

  isCommissionDateFormControlInvalid(): boolean {
    return this.getCommissionDateFormControl()?.invalid;
  }

  getCommissionDateFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.CommissionDate,
    ) as FormControl;
  }

  getCommissionDateUnknownFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.CommissionDateUnknown,
    ) as FormControl;
  }

  isLoadTimeDescriptionFormControlValid(): boolean {
    return this.getLoadTimeDescriptionFormControl()?.valid;
  }

  getLoadTimeDescriptionFormControl(): FormControl {
    return this.getCombustionSourceFormGroup()?.get(
      this.combustionSourceControlName.LoadTimeDescription,
    ) as FormControl;
  }

  getCombustionSourceFormGroup(): FormGroup {
    return this.formGroup.get(this.controlName.CombustionSource) as FormGroup;
  }

  shouldShowRegonAlert(): boolean {
    return this.formAlertService.shouldShowErrorAlert(
      this.getRegonFormControl(),
      'regon',
      this.submitted,
    );
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
